import { baseApi } from "@/services/api";
import { store } from "@/app/store";
import type { AppDispatch } from "@/app/store";
import { DELETE_CONFIRM_TOKEN } from "@/features/catalog-lifecycle/catalogLifecycleTypes";
import type {
  CatalogBulkPurgeResponse,
  CatalogPurgePreflight,
} from "@/features/catalog-lifecycle/catalogLifecycleTypes";

// V2 Music Catalog · Artists API client.
//
// Backed by the Slice #1 backend in krosskast-v2-admin-api
// `kk-admin-sa-music-catalog`. Doctrine anchor:
// docs/architecture/MUSIC-CATALOG-DOMAIN.md §4.4 (canonical) + §12.2.E1
// (projection extension) per docs/slices/music-artists.md.
//
// IMPORTANT — read posture
//   * All read endpoints hit `kk-artist-catalog-projection-${stage}`
//     (HR-13: UI never reads canonical state directly).
//   * Mutations patch ALL cached list queries from the mutation response
//     (Active / Archived / All), then briefly poll projection GET until the
//     materializer catches up. We avoid invalidating the LIST tag on write —
//     an immediate projection refetch can race the stream and undo the
//     optimistic row.
//
// Lifecycle (owner 2026-07-25 — Song Library parity):
//   Active → Archive (soft delete, status='deleted') → (Restore | Permanent
//   Delete). Permanent Delete keeps the audit tombstone and hard-deletes the
//   canonical artist row. The UI always says "Archived" for status='deleted'.

export type ArtistStatus = "active" | "deleted";

export interface ArtistProRegistration {
  affiliation: string;
  registrationNumber?: string | null;
}

export interface Artist {
  artistId: string;
  name: string;
  nameLower: string;
  // Optional Person-identity metadata layered on the canonical `name`.
  // Either, both, or neither may be present. `name` remains the single
  // display identity; when both first/last are present and the wire
  // `name` was blank, the backend derives `name = ${firstName} ${lastName}`.
  firstName?: string | null;
  lastName?: string | null;
  status: ArtistStatus;
  biography?: string | null;
  externalLinks?: Record<string, string> | null;
  proRegistration?: ArtistProRegistration | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  // Projection-only marker.
  materializedAt?: string;
  // Sparse fields on canonical-state row.
  createdBySub?: string;
  updatedBySub?: string;
  deletedAt?: string | null;
  deletedBySub?: string | null;
  // CAT-1b immutable provenance ("pipeline" | "user" | "import" | "system").
  createdBySource?: string | null;
  sourceProcessingJobId?: string | null;
  /** Active Songs only (authoritative server count). */
  activeSongCount?: number;
}

export interface ListArtistsArgs {
  status?: ArtistStatus | "all";
  nameStartsWith?: string;
  limit?: number;
  pageToken?: string;
}

export interface ListArtistsResponse {
  artists: Artist[];
  pageToken: string | null;
  limit: number;
  status: string;
}

export interface CreateArtistArgs {
  // At least one of `name`, `firstName`, `lastName` is required by the
  // backend. If `name` is omitted/blank and first/last has content, the
  // backend derives `name` server-side.
  name?: string;
  firstName?: string;
  lastName?: string;
  biography?: string;
  externalLinks?: Record<string, string>;
}

export interface UpdateArtistArgs {
  artistId: string;
  // PATCH-classic: absent leaves the field unchanged; "" / null clears.
  // Explicit `name=""` triggers backend re-derivation from first/last.
  name?: string;
  firstName?: string | null;
  lastName?: string | null;
  biography?: string | null;
  externalLinks?: Record<string, string> | null;
}

export interface DeleteArtistArgs {
  artistId: string;
}

/** Soft archive — same DELETE route; named for Song Library parity. */
export type ArchiveArtistArgs = DeleteArtistArgs;

export interface RestoreArtistArgs {
  artistId: string;
}

export interface RestoreArtistResponse {
  artistId: string;
  status: "active";
  restoredAt: string;
  restoredBy?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  version: number;
}

export interface PurgeArtistArgs {
  artistId: string;
  reason: string;
  confirmName?: string;
  confirmArtistId?: string;
}

// Server envelope: { success: boolean, data?: T, message?: string }.
interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

const PROJECTION_SYNC_MAX_ATTEMPTS = 12;
const PROJECTION_SYNC_INTERVAL_MS = 400;

function artistTimestampMs(artist: Pick<Artist, "updatedAt">): number {
  const parsed = Date.parse(artist.updatedAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isArtistAtLeastAsNew(
  candidate: Artist,
  existing: Artist | undefined,
): boolean {
  if (!existing) return true;
  if (candidate.version > existing.version) return true;
  if (candidate.version < existing.version) return false;
  return artistTimestampMs(candidate) >= artistTimestampMs(existing);
}

function patchAllArtistsLists(
  dispatch: AppDispatch,
  mutator: (draft: ListArtistsResponse, listArgs: ListArtistsArgs) => void,
) {
  const cachedArgs = artistsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listArtists",
  );
  for (const args of cachedArgs) {
    const listArgs = (args ?? {}) as ListArtistsArgs;
    dispatch(
      artistsApi.util.updateQueryData("listArtists", args, (draft) => {
        mutator(draft, listArgs);
      }),
    );
  }
}

function applyArtistToListCache(
  draft: ListArtistsResponse,
  artist: Artist,
  listArgs: ListArtistsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  const idx = draft.artists.findIndex((row) => row.artistId === artist.artistId);
  const existing = idx >= 0 ? draft.artists[idx] : undefined;
  const merged: Artist =
    typeof artist.activeSongCount === "number" ||
    !existing ||
    typeof existing.activeSongCount !== "number"
      ? artist
      : { ...artist, activeSongCount: existing.activeSongCount };

  if (merged.status === "deleted") {
    if (filterStatus === "active") {
      if (idx >= 0) draft.artists.splice(idx, 1);
      return;
    }
    if (filterStatus === "deleted" || filterStatus === "all") {
      if (existing && !isArtistAtLeastAsNew(merged, existing)) return;
      if (idx >= 0) draft.artists[idx] = merged;
      else draft.artists.unshift(merged);
    }
    return;
  }

  if (filterStatus === "deleted") {
    if (idx >= 0) draft.artists.splice(idx, 1);
    return;
  }

  if (existing && !isArtistAtLeastAsNew(merged, existing)) return;
  if (idx >= 0) draft.artists[idx] = merged;
  else draft.artists.unshift(merged);
}

function removeArtistFromListCache(
  draft: ListArtistsResponse,
  artistId: string,
  listArgs: ListArtistsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.artists.findIndex((row) => row.artistId === artistId);
    if (idx >= 0) {
      draft.artists[idx] = { ...draft.artists[idx], status: "deleted" };
    }
    return;
  }
  draft.artists = draft.artists.filter((row) => row.artistId !== artistId);
}

function restoreArtistInListCache(
  draft: ListArtistsResponse,
  artistId: string,
  listArgs: ListArtistsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.artists.findIndex((row) => row.artistId === artistId);
    if (idx >= 0) {
      draft.artists[idx] = { ...draft.artists[idx], status: "active" };
    }
    return;
  }
  if (filterStatus === "deleted") {
    draft.artists = draft.artists.filter((row) => row.artistId !== artistId);
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Poll projection GET until the row is visible, then merge into every list cache. */
function scheduleProjectionUpsert(dispatch: AppDispatch, artistId: string) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            artistsApi.endpoints.getArtist.initiate(
              { artistId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        patchAllArtistsLists(dispatch, (draft, listArgs) => {
          applyArtistToListCache(draft, projected, listArgs);
        });
        return;
      } catch {
        // Projection row not visible yet — list cache keeps the canonical patch.
      }
    }
  })();
}

/** Poll projection GET until the active row is gone (soft-archive materialized). */
function scheduleProjectionRemoval(dispatch: AppDispatch, artistId: string) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            artistsApi.endpoints.getArtist.initiate(
              { artistId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        if (projected.status === "deleted") {
          patchAllArtistsLists(dispatch, (draft, listArgs) => {
            removeArtistFromListCache(draft, artistId, listArgs);
          });
          return;
        }
      } catch {
        patchAllArtistsLists(dispatch, (draft, listArgs) => {
          removeArtistFromListCache(draft, artistId, listArgs);
        });
        return;
      }
    }
  })();
}

export const artistsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listArtists: builder.query<ListArtistsResponse, ListArtistsArgs | void>({
      query: (args) => {
        const params: Record<string, string | number> = {};
        if (args && args.status) params.status = args.status;
        if (args && args.nameStartsWith) params.nameStartsWith = args.nameStartsWith;
        if (args && args.limit) params.limit = args.limit;
        if (args && args.pageToken) params.pageToken = args.pageToken;
        return {
          url: "/artists",
          method: "GET",
          params,
        };
      },
      transformResponse: (raw: ApiEnvelope<ListArtistsResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to load artists");
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.artists
          ? [
              ...result.artists.map((a) => ({ type: "Artists" as const, id: a.artistId })),
              { type: "Artists" as const, id: "LIST" },
            ]
          : [{ type: "Artists" as const, id: "LIST" }],
    }),
    getArtist: builder.query<Artist, { artistId: string; includeDeleted?: boolean }>({
      query: ({ artistId, includeDeleted }) => ({
        url: `/artists/${encodeURIComponent(artistId)}`,
        method: "GET",
        params: includeDeleted ? { includeDeleted: "true" } : {},
      }),
      transformResponse: (raw: ApiEnvelope<{ artist: Artist }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.artist) {
          throw new Error(raw && raw.message ? raw.message : "Failed to load artist");
        }
        return raw.data.artist;
      },
      providesTags: (_result, _error, { artistId }) => [
        { type: "Artists" as const, id: artistId },
      ],
    }),
    createArtist: builder.mutation<Artist, CreateArtistArgs>({
      query: (body) => ({
        url: "/artists",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ artist: Artist }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.artist) {
          throw new Error(raw && raw.message ? raw.message : "Failed to create artist");
        }
        return raw.data.artist;
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Artists", id: result.artistId }] : [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          patchAllArtistsLists(dispatch, (draft, listArgs) => {
            applyArtistToListCache(draft, created, listArgs);
          });
          scheduleProjectionUpsert(dispatch, created.artistId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    updateArtist: builder.mutation<Artist, UpdateArtistArgs>({
      query: ({ artistId, ...patch }) => ({
        url: `/artists/${encodeURIComponent(artistId)}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: (raw: ApiEnvelope<{ artist: Artist }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.artist) {
          throw new Error(raw && raw.message ? raw.message : "Failed to update artist");
        }
        return raw.data.artist;
      },
      invalidatesTags: (_result, _error, { artistId }) => [
        { type: "Artists", id: artistId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          patchAllArtistsLists(dispatch, (draft, listArgs) => {
            applyArtistToListCache(draft, updated, listArgs);
          });
          scheduleProjectionUpsert(dispatch, updated.artistId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    /**
     * Soft archive. Endpoint name kept as `deleteArtist` for existing call
     * sites; the page surfaces it as Archive. Wire status becomes `deleted`.
     */
    deleteArtist: builder.mutation<
      { artistId: string; status: ArtistStatus; deletedAt: string; version: number },
      DeleteArtistArgs
    >({
      query: ({ artistId }) => ({
        url: `/artists/${encodeURIComponent(artistId)}`,
        method: "DELETE",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          artistId: string;
          status: ArtistStatus;
          deletedAt: string;
          version: number;
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to archive artist");
        }
        return raw.data;
      },
      invalidatesTags: (_result, _error, { artistId }) => [
        { type: "Artists", id: artistId },
      ],
      async onQueryStarted({ artistId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllArtistsLists(dispatch, (draft, listArgs) => {
            removeArtistFromListCache(draft, artistId, listArgs);
          });
          scheduleProjectionRemoval(dispatch, artistId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    restoreArtist: builder.mutation<RestoreArtistResponse, RestoreArtistArgs>({
      query: ({ artistId }) => ({
        url: `/artists/${encodeURIComponent(artistId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreArtistResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to restore artist",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ artistId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllArtistsLists(dispatch, (draft, listArgs) => {
            restoreArtistInListCache(draft, artistId, listArgs);
          });
          scheduleProjectionUpsert(dispatch, artistId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    preflightPurgeArtist: builder.mutation<
      CatalogPurgePreflight,
      { artistId: string }
    >({
      query: ({ artistId }) => ({
        url: `/artists/${encodeURIComponent(artistId)}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogPurgePreflight>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load artist deletion preflight",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
    }),
    purgeArtist: builder.mutation<
      {
        artistId: string;
        name?: string;
        purged: boolean;
        dependencyCheck?: {
          checkStatus: string;
          associatedTrackCount: number;
          message?: string;
        };
      },
      PurgeArtistArgs
    >({
      query: ({ artistId, reason, confirmName, confirmArtistId }) => ({
        url: `/artists/${encodeURIComponent(artistId)}/purge`,
        method: "POST",
        body: {
          confirm: DELETE_CONFIRM_TOKEN,
          reason,
          ...(confirmName ? { confirmName } : {}),
          ...(confirmArtistId ? { confirmArtistId } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          artistId: string;
          name?: string;
          purged: boolean;
          dependencyCheck?: {
            checkStatus: string;
            associatedTrackCount: number;
            message?: string;
          };
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to purge artist",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ artistId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllArtistsLists(dispatch, (draft) => {
            draft.artists = draft.artists.filter(
              (row) => row.artistId !== artistId,
            );
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    bulkPurgeArtists: builder.mutation<
      CatalogBulkPurgeResponse,
      { ids: string[]; reason: string }
    >({
      query: ({ ids, reason }) => ({
        url: "/artists/permanent-delete",
        method: "POST",
        body: { ids, confirm: DELETE_CONFIRM_TOKEN, reason },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogBulkPurgeResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to permanently delete artists",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const purgedIds = (data.results || [])
            .filter((row) => row.ok)
            .map((row) => row.id);
          if (purgedIds.length === 0) return;
          patchAllArtistsLists(dispatch, (draft) => {
            draft.artists = draft.artists.filter(
              (row) => !purgedIds.includes(row.artistId),
            );
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
  }),
});

export const {
  useListArtistsQuery,
  useGetArtistQuery,
  useCreateArtistMutation,
  useUpdateArtistMutation,
  useDeleteArtistMutation,
  useRestoreArtistMutation,
  usePreflightPurgeArtistMutation,
  usePurgeArtistMutation,
  useBulkPurgeArtistsMutation,
} = artistsApi;

/** Song Library naming alias — soft archive is DELETE /artists/{id}. */
export const useArchiveArtistMutation = useDeleteArtistMutation;

export function extractArtistErrorMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (
      data &&
      typeof data === "object" &&
      typeof data.message === "string" &&
      data.message.length > 0
    ) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}
